/**
 * Formation positions on the existing full-size stadium pitch (105×68).
 *
 * READ THIS BEFORE CHANGING A NUMBER. These slots are ABSOLUTE metres authored for a
 * 105×68 pitch: the keeper sits at x = −50, the lines at −35/−21/−7, wide players at
 * z = ±29. The pitch the app DRAWS is not that size - Viewport3D.js:452 overrides
 * createFootballPitch to 40×20 (box 10.0×5.5, goal 3.5), even though that helper
 * defaults to 105×68 with real FIFA box dimensions. So placing a 4-4-2 puts the keeper
 * 30 m outside the touchline and the wingers 19 m outside it. Measured live: players at
 * x −50..−7, z −29..+29 on turf spanning x −20..+20, z −10..+10.
 *
 * Two ways to resolve it, and which is right is a product decision, not a code one:
 * make the drawn pitch 105×68 (one line at Viewport3D.js:452 - this file, its tests and
 * createFootballPitch all already assume it), or rescale these slots to 40×20 (which
 * makes the shapes small-sided, and invalidates GK_X_HOME = −50 in formations.test.js).
 * Do not "fix" one side without deciding the other.
 *
 * Home defends −x; away defends +x. Slots fill from keeper to forwards.
 * Keep players inside their own half, with wide players near the touchlines.
 */

/** One line of the shape: an x, and the z of each player in it, own-view left→right. */
const line = (x, ...z) => z.map(zz => [x, zz]);

/** The keeper stands 2.5 m in front of the goal line. */
const GK = [-50, 0];

/** Eleven slots, with distinct defensive, midfield and forward lines. */
const SHAPES = {
  '442': [
    GK,
    ...line(-35, -29, -10, 10, 29),
    ...line(-21, -29, -10, 10, 29),
    ...line(-7, -12, 12),
  ],
  '433': [
    GK,
    ...line(-35, -29, -10, 10, 29),
    ...line(-21, -20, 0, 20),
    ...line(-7, -29, 0, 29),
  ],
  '4231': [
    GK,
    ...line(-35, -29, -10, 10, 29),
    ...line(-25, -14, 14),
    ...line(-15, -29, 0, 29),
    ...line(-5, 0),
  ],
  '352': [
    GK,
    ...line(-35, -22, 0, 22),
    ...line(-21, -29, -14, 0, 14, 29),
    ...line(-7, -12, 12),
  ],
  '532': [
    GK,
    ...line(-35, -29, -14, 0, 14, 29),
    ...line(-21, -20, 0, 20),
    ...line(-7, -12, 12),
  ],
};
/** The shapes in the order a coach reads them: most common first. */
export const FORMATION_KEYS = Object.freeze(['442', '433', '4231', '352', '532']);

/** How a shape is written on a team sheet. */
export function formationLabel(key) {
  const s = String(key).replace(/[^0-9]/g, '');
  return s ? s.split('').join('-') : String(key);
}

/**
 * The eleven slot positions for a side, in [x, y, z] pitch coordinates.
 * AWAY is the mirror image: the shape faces its own goal, whichever way the
 * team attacks.
 */
export function formationSlots(key, side) {
  const shape = SHAPES[key] || SHAPES['442'];
  const flip = side === 'away' ? -1 : 1;
  return shape.map(([x, z]) => [flip * x, 0, z]);
}

/**
 * Who goes where. `count` is how many players the side actually has; the shape
 * is filled from the GK up and stops when the squad runs out, so a seven-player
 * team gets the keeper, the back four and the first two of the next line.
 *
 * Returns [{ index, position }] — never more than eleven, and never an index
 * that does not exist on the team sheet.
 */
export function planFormation(key, side, count) {
  const n = Math.max(0, Math.min(11, Number(count) || 0));
  const slots = formationSlots(key, side);
  const out = [];
  for (let i = 0; i < n; i++) out.push({ index: i, position: slots[i] });
  return out;
}

/**
 * The gap that separates two lines, in metres. The authored shapes space their
 * lines 3 m apart at the tightest — 4231 puts its two midfield bands exactly
 * 3 m apart — so the cut sits under that, while still holding a line a coach
 * has dragged about (one that curves, or whose nearest neighbours sit a metre or
 * two off each other) together as ONE line.
 */
export const BAND_GAP_M = 2.5;

/**
 * The lines a side is STANDING IN, not the lines its formation key claims.
 * Players get moved by hand, drilled out of shape, and set up without ever
 * picking a shape; the truth is what the pitch shows. So: measure each player's
 * depth from their own goal and sort, cutting wherever the gap between
 * neighbours passes BAND_GAP_M.
 *
 * DEPTH IS x, not z. The shapes above are written [x, z] with the keeper at
 * x = −19, and formationSlots mirrors AWAY by flipping x — so the goal-to-goal
 * axis is x and z is the lateral one. Reading the wrong axis would call the
 * back four a line of players standing side by side across the pitch.
 *
 * Returns bands ordered from own goal outward, each an array of
 * { index, depth }. A player with no position on the pitch is in no band —
 * a bench slot is not a line, and selecting it would put a ghost in the group.
 */
export function playerBands(side, players) {
  const placed = (players || []).map((p, index) => {
    const position = p?.position;
    const x = Array.isArray(position) ? position[0] : NaN;
    return Number.isFinite(x) ? { index, depth: side === 'away' ? -x : x } : null;
  }).filter(Boolean).sort((a, b) => a.depth - b.depth);
  const bands = [];
  for (const p of placed) {
    const last = bands[bands.length - 1];
    // Measured from the band's DEEPEST member, not its last: a line of four
    // spread over 2 m stays one line, and the midfielder 3 m behind them cannot
    // chain the band forward into a fifth row.
    if (last && p.depth - last[0].depth <= BAND_GAP_M) last.push(p);
    else bands.push([p]);
  }
  return bands;
}

/**
 * The band one player is standing in — the line a coach would say out loud.
 * Returns [] when the player has no position, or is alone in its band: a band
 * of one is not a line, and there is nothing to select as a unit.
 */
export function bandOfPlayer(side, players, index) {
  for (const band of playerBands(side, players)) {
    if (band.some(p => p.index === index) && band.length >= 2) return band;
  }
  return [];
}
